<template>
  <div class="max-w-full mx-auto px-6 py-6 bg-white dark:bg-gray-600 dark:border-gray-600">
    <h2 class="text-2xl font-semibold mb-5 dark:text-gray-100 text-center">Edit Ticket</h2>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-12">
      <div class="space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Submitted Date -->
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Submitted Date</label>
            <input type="datetime-local" v-model="ticket.submitted" :max="maxSubmittedDateTime"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
          </div>

          <!-- Client -->
          <div class="md:col-span-2" v-click-outside="closeClientDropdown">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-100 mr-4">Client</label>
            <div class="relative">
              <input type="text" @focus="dropdownState.client.visible = true"
                @keydown.down.prevent="onKeyDown('client', 'down')" @keydown.up.prevent="onKeyDown('client', 'up')"
                @keydown.enter.prevent="onEnter('client', setClient, null)"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100"
                placeholder="Client Name" v-model="ticket.companyname" />
              <button v-if="ticket.company_id != '' && ticket.companyname !== ''" @click="clearClient"
                class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500">
                <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </button>
            </div>
            <ul v-if="dropdownState.client.visible" data-field="client"
              class="absolute z-10 bg-white mt-1 rounded-md shadow-lg w-100 dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 scrollable-requester-list">
              <li v-for="(client, index) in filteredClients" :key="client.id" @click="setClient(client)" :class="[
                'cursor-pointer p-3',
                dropdownState.client.index === index ? 'bg-blue-100 dark:bg-gray-700 active-item' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
              ]">
                {{ client.companyname }}<span v-if="client.active == 0" class="ml-2 text-xs italic text-gray-400">(inactive)</span>
              </li>
            </ul>
          </div>


          <!-- Type -->
          <div class="md:col-span-2" v-click-outside="closeTypeDropdown">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Type</label>
            <div class="relative">
              <input type="text" @focus="dropdownState.type.visible = true"
                @keydown.down.prevent="onKeyDown('type', 'down')" @keydown.up.prevent="onKeyDown('type', 'up')"
                @keydown.enter.prevent="onEnter('type', setReqType, null)"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100"
                placeholder="Type" v-model="ticket.tickettype" />
              <button v-if="ticket.tickettype != '' && ticket.tickettypeid !== ''" @click="clearReqType"
                class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500">
                <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </button>
            </div>
            <ul v-if="dropdownState.type.visible" data-field="type"
              class="absolute z-10 bg-white mt-1 rounded-md shadow-lg w-100 dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 scrollable-requester-list">
              <li v-for="(reqtype, index) in filteredReqTypes" :key="reqtype.id" @click="setReqType(reqtype)" :class="[
                'cursor-pointer p-3',
                dropdownState.type.index === index ? 'bg-blue-100 dark:bg-gray-700 active-item' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
              ]">
                {{ reqtype.description }}
              </li>
            </ul>
          </div>

          <!-- Requester -->
          <div class="md:col-span-2 mt-2">
            <div
              class="p-4 bg-white shadow-md rounded-md border-2 border-blue-900 relative dark:text-gray-100 dark:bg-gray-400 dark:border-gray-800">
              <div class="absolute top-0 left-0 right-0 bg-blue-900 px-3 py-1 flex items-center dark:bg-gray-800">
                <div class="flex items-center space-x-2">
                  <h4 class="text-white font-bold text-sm">Requested By</h4>
                </div>
              </div>
              <div class="md:col-span-2 mt-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-1">
                  <div class="flex space-x-2">
                    <div class="flex-grow" v-click-outside="closeReqDropdown">
                      <!-- <label class="block text-sm font-medium text-gray-700">Requested By</label> -->
                      <div class="relative">
                        <input type="text"
                          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 dark:placeholder-gray-100"
                          placeholder="Select Requester" v-model="ticket.requester"
                          @focus="dropdownState.requester.visible = true"
                          @keydown.down.prevent="onKeyDown('requester', 'down')"
                          @keydown.up.prevent="onKeyDown('requester', 'up')"
                          @keydown.enter.prevent="onEnter('requester', confirmRequesterChange, null, false)" />
                        <button v-if="ticket.requester !== '' && ticket.contact_id !== ''" @click="clearReq"
                          class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500">
                          <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                              stroke-linejoin="round" />
                            <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                              stroke-linejoin="round" />
                          </svg>
                        </button>
                      </div>
                      <ul v-if="dropdownState.requester.visible" data-field="requester"
                        class="absolute z-10 bg-white mt-1 rounded-md shadow-lg w-100 dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 scrollable-requester-list">
                        <li v-for="(req, index) in filteredRequesters" :key="req.id"
                          @click="confirmRequesterChange(req, false)" :class="[
                            'cursor-pointer p-3',
                            dropdownState.requester.index === index ? 'bg-blue-100 dark:bg-gray-700 active-item' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                          ]">
                          {{ req.name }}<span v-if="req.email"> - {{ req.email }}</span><span v-if="req.title"> ({{ req.title }})</span><span v-if="req.user_type"> ({{
                            req.user_type }})</span><span v-if="requesterCompanyLabel(req)" :title="requesterCompanyTitle(req)"> ({{ requesterCompanyLabel(req)
                            }})</span>
                        </li>
                      </ul>
                    </div>
                    <div class="shrink-0">
                      <button @click="showAddUserModal = true"
                        class="mt-2 bg-gray-400 p-1 border rounded text-xs leading-none dark:text-gray-100 dark:border-gray-900 dark:bg-gray-900">
                        <!-- Plus icon -->
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                          stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div v-click-outside="closeIntReqDropdown" v-if="!isCoManagedTech">
                    <!-- <label class="block text-sm font-medium text-gray-700">Requested By</label> -->
                    <div class="relative">
                      <input type="text"
                        class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 dark:placeholder-gray-100"
                        placeholder="Select Internal Requester" v-model="ticket.intrequester"
                        @focus="dropdownState.internalRequester.visible = true"
                        @keydown.down.prevent="onKeyDown('internalRequester', 'down')"
                        @keydown.up.prevent="onKeyDown('internalRequester', 'up')"
                        @keydown.enter.prevent="onEnter('internalRequester', confirmRequesterChange, null, true)" />
                      <button v-if="ticket.intrequester !== '' && ticket.contact_id !== ''" @click="clearIntReq"
                        class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500">
                        <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                            stroke-linejoin="round" />
                          <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                            stroke-linejoin="round" />
                        </svg>
                      </button>
                    </div>
                    <ul v-if="dropdownState.internalRequester.visible" data-field="internalRequester"
                      class="absolute z-10 bg-white mt-1 rounded-md shadow-lg w-100 dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 scrollable-requester-list">
                      <li v-for="(req, index) in filteredIntRequesters" :key="req.id"
                        @click="confirmRequesterChange(req, true)" :class="[
                          'cursor-pointer p-3',
                          dropdownState.internalRequester.index === index ? 'bg-blue-100 dark:bg-gray-700 active-item' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                        ]">
                        {{ req.name }}<span v-if="req.email"> - {{ req.email }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <RequesterConfirmationModal v-if="showReqConfirmationModal" @confirm="handleReqConfirmation(true)"
                @cancel="handleReqConfirmation(false)" />
              <div class="md:col-span-2 mt-4">
                <div class="grid grid-cols-1 md:grid-cols-1 gap-1">
                  <div>
                    <div class="flex justify-left mt-2">
                      <label class="block font-medium text-gray-700 text-xs dark:text-gray-100 mt-2 mr-2">Add a
                        Watcher?</label>
                      <button @click="toggleWatcher"
                        class="mt-1 bg-gray-400 p-1 border rounded text-xs leading-none dark:text-gray-100 dark:border-gray-900 dark:bg-gray-900 transition-transform duration-300"
                        :class="{ 'transform rotate-180': showWatcher }">
                        <svg
                          :class="{ 'fill-current text-gray-100': !showWatcher, 'fill-current text-blue-500': showWatcher }"
                          xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" class="h-4 w-4">
                          <path
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                        </svg>
                      </button>
                    </div>
                    <transition name="slide-fade">
                      <div v-click-outside="closeWatcherDropdown">
                        <input v-if="showWatcher" type="email"
                          class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 dark:placeholder-gray-100 transition-all duration-300 ease-in-out"
                          placeholder="Type to search from the contact list"
                          @focus="dropdownState.watcher.visible = true"
                          @keydown.down.prevent="onKeyDown('watcher', 'down')"
                          @keydown.up.prevent="onKeyDown('watcher', 'up')"
                          @keydown.enter.prevent="onEnter('watcher', addWatcher, null)" v-model="watchersInput" />
                        <ul v-if="dropdownState.watcher.visible" data-field="watcher"
                          class="absolute z-10 bg-white mt-1 rounded-md shadow-lg w-100 dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 scrollable-requester-list">
                          <li v-for="(watcher, index) in filteredWatchers" :key="watcher.id"
                            @click="addWatcher(watcher)" :class="[
                              'cursor-pointer p-3',
                              dropdownState.watcher.index === index ? 'bg-blue-100 dark:bg-gray-700 active-item' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                            ]">
                            {{ watcher.name }}
                          </li>
                        </ul>
                        <div class="mt-1 grid grid-cols-1 sm:grid-cols-1 md:grid-cols-3 gap-1">
                          <div v-for="watcher in (ticket.watchersList || []).filter(w => w.visible)" :key="watcher.id"
                            class="flex justify-between items-center border border-gray-200 rounded-full p-1 text-xs dark:border-gray-900 dark:bg-gray-400">
                            {{ watcher.name }}
                            <button @click="removeWatcher(watcher)"
                              class="ml-2 bg-red-600 hover:bg-red-700 text-white p-1 rounded-full text-xs">
                              &times;
                            </button>
                          </div>
                        </div>
                      </div>
                    </transition>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <AddRequesterModal :isVisible="showAddUserModal" @close="showAddUserModal = false"
            @complete="handleAddUserCompletion" />
          <!-- Email, Phone -->
          <div class="flex space-x-2">
            <div class="mt-2 flex-grow">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Email</label>
              <input type="email"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100"
                placeholder="Email" v-model="ticket.email" @focus="handleFocus('email')"
                @change="handleBlur('email')" />
              <p v-if="notifyBlocked" class="mt-1 text-xs text-gray-500 dark:text-gray-300">
                Requester isn't emailed for private or internal tickets.
              </p>
            </div>
            <div class="shrink-0 mt-8">
              <input type="checkbox" id="notifyCheckbox"
                class="form-checkbox h-4 w-4 text-blue-600 dark:text-gray-900 rounded" v-model="notifyCheckboxModel"
                :disabled="notifyBlocked" :class="{ 'opacity-50 cursor-not-allowed': notifyBlocked }" />
              <label for="notifyCheckbox" class=" ml-1 text-xs font-medium text-gray-700 dark:text-gray-100">
                Notify
              </label>
            </div>
          </div>
          <div class="flex space-x-2">
            <div class="mt-2 flex-grow">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Phone Number</label>
              <input type="email"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100"
                placeholder="xxxxxxxxxx" v-model="ticket.alternatephone" @focus="handleFocus('phone')"
                @change="handleBlur('phone')" />
            </div>
            <div class="shrink-0 mt-8">
              <div class="flex flex-col space-y-2">
                <div class="flex items-center">
                  <input type="checkbox" id="publishCheckbox"
                    class="form-checkbox h-4 w-4 text-blue-600 dark:text-gray-900 rounded"
                    v-model="unPublishedCheckboxModel" />
                  <label for="publishCheckbox" class="ml-1 text-xs font-medium text-gray-700 dark:text-gray-100">
                    DO NOT Publish
                  </label>
                </div>
                <div class="flex items-center">
                  <input type="checkbox" id="internalCheckbox"
                    class="form-checkbox h-4 w-4 text-blue-600 dark:text-gray-900 rounded"
                    v-model="internalCheckboxModel" />
                  <label for="internalCheckbox" class="ml-1 text-xs font-medium text-gray-700 dark:text-gray-100">
                    Internal
                  </label>
                </div>
              </div>
            </div>
          </div>
          <ConfirmationModal :isVisible="showConfirmationModal" :field="fieldToConfirm" :value="valueToConfirm"
            @confirm="handleUpdateUserConfirm" @cancel="handleUpdateUserCancel" />
          <PhoneOverrideConfirmationModal :isVisible="showPhoneOverrideModal" :newPhone="ticket.alternatephone || ''"
            @decision="handlePhoneOverrideDecision" />

          <!-- Description -->
          <div class="md:col-span-2 relative">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Description</label>
            <textarea v-model="ticket.description"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100"></textarea>
            <button @click="activateTextArea('requester')"
              class="absolute right-2 bg-gray-400 bottom-2 p-1 border rounded text-xs leading-none dark:text-gray-100 dark:border-gray-900 dark:bg-gray-900"
              style="margin-right: 1rem">
              [ ]
            </button>
          </div>

          <!-- Assign To, Source -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Source</label>
            <select
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100"
              v-model="ticket.source">
              <!-- Loop through ticket sources and create an option for each source -->
              <option v-for="source in ticketSources" :value="source.name" :key="source.name">
                {{ source.name }}
              </option>
            </select>
          </div>
          <div class="flex space-x-2">
            <div class="flex-grow">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Assign To</label>
              <select v-model="ticket.tech_id"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
                <option value="0">Send to Delegator</option>
                <template v-for="(techList, deptName) in groupedTechnicians" :key="deptName">
                  <optgroup v-if="deptName" :label="deptName">
                    <option v-for="tech in techList" :key="tech.id" :value="tech.id">
                      {{ tech.name }}
                    </option>
                  </optgroup>
                  <template v-else>
                    <option v-for="tech in techList" :key="tech.id" :value="tech.id">
                      {{ tech.name }}
                    </option>
                  </template>
                </template>
              </select>
            </div>
            <div class="shrink-0 mt-6">
              <input type="checkbox" id="urgentCheckbox"
                class="form-checkbox h-4 w-4 text-blue-600 dark:text-gray-900 rounded" v-model="urgencyCheckboxModel" />
              <label for="urgentCheckbox" class=" ml-1 text-xs font-medium text-gray-700 dark:text-gray-100">
                Urgent
              </label>
            </div>
          </div>

          <div class="md:col-span-2 mt-2">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-1">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Select Ticket Status</label>
                <select v-model="ticket.ticketstatus_id"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
                  <option v-for="ticketstatus in ticketstatuses" :key="ticketstatus.id" :value="ticketstatus.id">{{
                    ticketstatus.name }}</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Select Code Status</label>
                <select v-model="ticket.codestatus_id"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
                  <option v-for="codestatus in codestatuses" :key="codestatus.id" :value="codestatus.id">{{
                    codestatus.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">

          <div class="md:col-span-2 mt-2">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-1">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Select Project</label>
                <select v-model="ticket.project_id"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
                  <option v-for="project in projects" :key="project.id" :value="project.id">{{ project.name }}</option>
                </select>
              </div>
              <div v-if="!userroles.includes(80)">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Select Sprint</label>
                <select v-model="ticket.sprint_id"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
                  <option value="0">0 (Backlog)</option>
                  <option v-for="sprint in filteredSprints" :key="sprint.id" :value="sprint.id">
                    {{ sprint.name }} ({{ sprint.startdate }} to {{ sprint.enddate }})
                  </option>
                </select>
              </div>
            </div>
          </div>

          <!-- Notes/Direction -->
          <div class="md:col-span-2 relative">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Notes/Direction (Public)</label>
            <textarea v-model="ticket.delegatornotes"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100"></textarea>
            <button @click="activateTextArea('aimNotesPublic')"
              class="absolute right-2 bg-gray-400 bottom-2 p-1 border rounded text-xs leading-none dark:text-gray-100 dark:border-gray-900 dark:bg-gray-900"
              style="margin-right: 1rem">
              [ ]
            </button>
          </div>

          <!-- Private Info -->
          <div class="md:col-span-2 relative">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Private Info</label>
            <textarea v-model="ticket.privateinfo"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100"></textarea>
            <button @click="activateTextArea('privateNotes')"
              class="absolute right-2 bg-gray-400 bottom-2 p-1 border rounded text-xs leading-none dark:text-gray-100 dark:border-gray-900 dark:bg-gray-900"
              style="margin-right: 1rem">
              [ ]
            </button>
          </div>


          <!-- Time frames -->
          <!-- <div class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">When would you like this request
              handled?</label>
            <select v-model="ticket.selectedTimeFrameID"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
              <option v-for="type in timeFrames" :key="type.id" :value="type.id">{{ type.label }}</option>
            </select>
          </div> -->

          <!-- Promised -->
          <div class="md:col-span-2 mt-2">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-1">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Promised</label>
                <input type="datetime-local"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100"
                  v-model="ticket.promised" />
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-1">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Day</label>
                  <select
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100"
                    v-model="selectedDay">
                    <option value="-1" disabled>Select a Quick Day</option>
                    <option value="0">Today</option>
                    <option value="1">Tomorrow</option>
                    <option value="2">2 Days</option>
                    <option value="3">3 Days</option>
                    <option value="4">4 Days</option>
                    <option value="5">5 Days</option>
                    <option value="6">6 Days</option>
                    <option value="7">1 Week</option>
                    <option value="14">2 Weeks</option>
                    <option value="21">3 Weeks</option>
                    <option value="30">1 Month</option>
                    <option value="60">2 Months</option>
                    <option value="90">3 Months</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Time</label>
                  <select
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100"
                    v-model="selectedTime">
                    <option value="-1" disabled>Select a Quick Time</option>
                    <option value="07:00">7:00</option>
                    <option value="08:00">8:00</option>
                    <option value="09:00">9:00</option>
                    <option value="10:00">10:00</option>
                    <option value="11:00">11:00</option>
                    <option value="12:00">12:00</option>
                    <option value="13:00">1:00</option>
                    <option value="14:00">2:00</option>
                    <option value="15:00">3:00</option>
                    <option value="16:00">4:00</option>
                    <option value="17:00">5:00</option>
                    <option value="18:00">6:00</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- Deadline -->
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Deadline</label>
            <input type="datetime-local" v-model="ticket.deadline"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
          </div>

          <!-- Estimated Hours -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-1">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Estimated Hours</label>
              <input type="number"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100"
                placeholder="Extimated Hours" v-model="ticket.esttime" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Select Estimated hours</label>
              <select
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100"
                v-model="ticket.esttime">
                <option value="0.1">0.1</option>
                <option value="0.25">0.25</option>
                <option value="0.50">0.50</option>
                <option value="0.75">0.75</option>
                <option value="1.00">1.00</option>
                <option value="1.50">1.50</option>
                <option value="2.00">2.00</option>
                <option value="3.00">3.00</option>
                <option value="4.00">4.00</option>
              </select>
            </div>
          </div>

          <!-- Attach Documents -->
          <div class="md:col-span-2 flex items-center">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Attach Document:</label>
            <input type="file" @change="handleFileUpload" ref="fileInput" multiple
              class="ml-2 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:text-gray-100" />
          </div>
          <!-- Display of uploaded documents -->
          <div class="md:col-span-2 mt-4" v-if="ticket.docs && ticket.docs.length > 0">
            <div
              class="p-4 bg-white shadow-md rounded-md border-2 border-blue-900 relative overflow-x-auto dark:text-gray-100 dark:bg-gray-400 dark:border-gray-800">
              <div
                class="absolute top-0 left-0 right-0 bg-blue-900 px-3 py-1 flex items-center dark:bg-gray-800 w-full">
                <div class="flex items-center space-x-2">
                  <h4 class="text-white font-bold text-xs">
                    Attached Documents
                  </h4>
                </div>
              </div>
              <div>
                <table class="min-w-full divide-y divide-gray-200 mt-4">
                  <thead>
                    <tr>
                      <th class="px-1 py-1 text-left text-xs font-medium text-gray-900 uppercase">
                        Name
                      </th>
                      <th class="px-1 py-1 text-left text-xs font-medium text-gray-900 uppercase">Preview</th>
                      <th class="px-1 py-1 text-left text-xs font-medium text-gray-900 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="doc in ticket.docs" :key="doc.id"
                      class="bg-white dark:text-gray-100 dark:bg-gray-400 dark:border-gray-800">
                      <td class="px-1 py-3 whitespace-nowrap text-xs text-gray-900">
                        <a :href="doc.download_url" :download="doc.doc_name" target="_blank" class="hover:underline">
                          {{ doc.doc_name }}
                        </a>
                      </td>
                      <td class="px-1 py-3 whitespace-nowrap text-xs text-gray-900">
                        <a v-if="doc.is_image" :href="doc.view_url" target="_blank" rel="noopener">
                          <img :src="doc.view_url" alt="" class="h-10 w-10 object-cover rounded border" />
                        </a>
                      </td>
                      <td class="px-1 py-3 whitespace-nowrap text-xs font-medium">
                        <button @click="deleteDocument(doc)" class="text-red-600 hover:text-red-900">Delete</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div v-if="ticket.attachTo && ticket.attachedRelatedTicket"
            class="md:col-span-2 mt-3 mb-2 p-3 rounded-md border-2 border-yellow-500 bg-yellow-50 dark:bg-yellow-900/30 dark:border-yellow-400">
            <div class="flex items-start justify-between gap-3">
              <div class="text-xs text-yellow-900 dark:text-yellow-100">
                <div class="font-semibold mb-1">
                  ⚠ Attached Related Ticket Selected
                </div>

                <div>
                  #{{ ticket.attachedRelatedTicket.id }}
                  {{ ticket.attachedRelatedTicket.tickettype }}
                  —
                  {{ ticket.attachedRelatedTicket.firstname }}
                  {{ ticket.attachedRelatedTicket.lastname }}
                </div>

                <div class="italic">
                  {{ ticket.attachedRelatedTicket.requester }}
                </div>

                <div class="mt-1">
                  <template v-if="ticket.attachedRelatedTicket.description?.includes(':')">
                    {{ ticket.attachedRelatedTicket.tickettype }} {{ ticket.attachedRelatedTicket.description }}
                  </template>
                  <template v-else>
                    {{ ticket.attachedRelatedTicket.tickettype }}: {{ ticket.attachedRelatedTicket.description }}
                  </template>
                </div>
              </div>

              <button type="button" @click="
                ticket.attachTo = 0;
              ticket.attachedRelatedTicket = null;
              " class="text-xs px-2 py-1 rounded bg-red-500 text-white hover:bg-red-600">
                Remove
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Submit Button -->
    <div class="md:col-span-2 text-center">
      <button type="submit" @click="submitTicket"
        class="mt-6 mr-2 px-4 py-2 border border-transparent text-base font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-700">Update
        Ticket</button>
      <button
        class="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-400 dark:hover:bg-blue-700"
        @click="openTicketsModal">
        Open related tickets
      </button>
    </div>
    <!-- Large Textarea Modal -->
    <LargeTextareaModal :isVisible="showTextArea" :initialText="getTextareaInitialText()"
      :activeTextarea="activeTextarea" @close="showTextArea = false" @complete="handleTextCompletion" />
    <!-- Modal -->
    <div v-if="showTicketsModal"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 overflow-y-auto h-full w-full flex justify-center items-center">
      <TicketsModal v-show="currentStep === 1" @close="handleClose()" :clientId="ticket.company_id"
        :attachTo="ticket.attachTo" :excludeTicketId="ticket.id" @handleRelatedTickets="handleRelatedTickets" />
    </div>
  </div>
</template>

<script>
import { requesterCompanyLabel, requesterCompanyTitle } from '@/utils/requesterCompanies';
import { searchRequesters } from '@/utils/requesterSearch';
import axios from 'axios';
import LargeTextareaModal from "@/views/modals/LargeTextareaModal.vue";
import { computed, watch, ref } from 'vue';
import AddRequesterModal from "@/views/modals/AddRequesterModal.vue";
import RequesterConfirmationModal from "@/views/modals/RequesterConfirmationModal.vue";
import ConfirmationModal from "@/views/modals/ConfirmationModal.vue";
import PhoneOverrideConfirmationModal from "@/views/modals/PhoneOverrideConfirmationModal.vue";
import TicketsModal from "@/views/modals/TicketsModal.vue";
import { useRoute } from 'vue-router';
import { useToast } from "vue-toastification";
import { useJwt } from '@vueuse/integrations/useJwt';
const fileInput = ref('');
const currentStep = ref(1);

export default {
  components: {
    LargeTextareaModal,
    AddRequesterModal,
    RequesterConfirmationModal,
    ConfirmationModal,
    PhoneOverrideConfirmationModal,
    TicketsModal
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      ticket: {
        companyname: '',
        submitted: '',
        company_id: '',
        contact_id: '',
        requester: '',
        intrequester: '',
        project_id: '',
        sprint_id: '',
        ticketstatus_id: '',
        codestatus_id: '',
        alternatephone: '',
        email: '',
        description: '',
        tickettype: '',
        tickettypeid: '',
        tech_id: '',
        // selectedTimeFrameID: '',
        delegatornotes: '',
        privateinfo: '',
        promised: '',
        docs: [],
        deadline: '',
        esttime: 0,
        urgent: 0,
        unpublished: 0,
        oldRequester: '',
        watchersList: [],
        emailnotification: 1,
        source: '',
        subTickets: [],
        deleteDocumentIds: [],
        attachTo: 0,
        internal: 0,
      },
      isInternalUser: false,
      currentStep: 1,
      ticketId: 0,
      activeClients: 'true',
      showWatcher: false,
      showWatcherDropdown: false,
      showReqDropdown: false,
      showIntReqDropdown: false,
      showAddUserModal: false,
      showReqConfirmationModal: false,
      showTicketsModal: false,
      showConfirmationModal: false,
      showPhoneOverrideModal: false,
      updateAddressbookPhone: 0,
      phoneOverrideDecisionMade: false,
      originalAlternatePhone: '',
      showTextArea: false,
      activeTextarea: '',
      watchersInput: '',
      selectedRequester: '',
      fieldToConfirm: '',
      valueToConfirm: '',
      clients: [],
      projects: [],
      userroles: [],
      sprints: [],
      ticketstatuses: [],
      codestatuses: [],
      requesters: [],
      intRequesters: [],
      allRequesters: [],
      ticketTypes: [],
      timeFrames: [],
      technicians: [],
      ticketSources: [],
      selectedDay: '-1',
      selectedTime: '-1',
      showClientDropdown: false,
      showReqTypeDropdown: false,
      timeframesMapping: {
        '1': this.getCurrentLocalTime(), // Immediately (current local time)
        '2': { days: 0, time: '12:00' }, // By noon today
        '3': { days: 0, time: '17:00' }, // By end of day
        '8': { days: this.getNextBusinessDayOffset(), time: '12:00' }, // Noon next business day
        '4': { days: this.getNextBusinessDayOffset(), time: '17:00' }, // End of next business day
        '5': this.getEndOfWeekFriday(), // By end of week (upcoming Friday 5 PM)
        '6': this.getEndOfNextWeekFriday(), // By end of next week (following Friday 5 PM)
        '7': { days: 0, time: '17:00' }, // No rush, whenever you can get to it
      },
      dropdownState: {
        client: { index: -1, items: [], visible: false },
        type: { index: -1, items: [], visible: false },
        requester: { index: -1, items: [], visible: false },
        internalRequester: { index: -1, items: [], visible: false },
        watcher: { index: -1, items: [], visible: false },
      }
    };
  },
  methods: {
    requesterCompanyLabel,
    requesterCompanyTitle,
    checkUserType() {
      // Internal standing comes from the token scopes (internal/extTech), not
      // the old "has no companies" heuristic — company-less contact accounts
      // must not count as internal. Fail closed if the token can't be decoded.
      try {
        const { payload } = useJwt(localStorage.getItem('token'));
        const scopes = payload.value?.scopes || [];
        this.isInternalUser = scopes.includes('internal') || scopes.includes('extTech');
      } catch (error) {
        this.isInternalUser = false;
      }
    },
    async fetchUserRoles() {
      this.$updateLoading(1);
      const user_id = localStorage.getItem('user_id');
      const res = await axios.get(`user/role/${user_id}`);
      this.userroles = res.data.roles || [];
      this.$updateLoading(-1);
    },
    async fetchInitialData() {
      this.$updateLoading(1);
      try {
        await this.fetchClients();

        axios.get(`ticket/status`)
          .then((response) => {
            this.ticketstatuses = response.data;
          })
          .catch((error) => {
            console.error("There was an error fetching ticket types:", error);
          });
        axios.get(`ticket/codestatus`)
          .then((response) => {
            this.codestatuses = response.data;
          })
          .catch((error) => {
            console.error("There was an error fetching ticket types:", error);
          });
        axios.get(`ticket/sprint`)
          .then((response) => {
            this.sprints = response.data;
          })
          .catch((error) => {
            console.error("There was an error fetching ticket types:", error);
          });
        axios.get(`requesters/`)
          .then(response => {
            this.requesters = response.data;
          })
          .catch(error => {
            console.error(error);
          });
        axios.get(`ticket/timeframes`)
          .then(response => {
            this.timeFrames = response.data;
          })
          .catch(error => {
            console.error(error);
          });
        axios.get(`ticket/sources`)
          .then(response => {
            this.ticketSources = response.data;
          })
          .catch(error => {
            console.error(error);
          });
        // Attachments are loaded by fetchTicket(), after the ticket object exists:
        // fetching them here raced the ticket request, and `this.ticket = response.data`
        // in fetchTicket() threw away whatever had already been attached to the old object.

        // Get all requesters
        // const requestersResponse = await axios.get(`requesters/`);
        // this.requesters = requestersResponse.data;

        // // Get active techs
        // // const techniciansResponse = await axios.get(`user/active-users`);
        // // this.technicians = techniciansResponse.data;

        // // Get ticket sources
        // const ticketSourcesResponse = await axios.get(`ticket/sources`);
        // this.ticketSources = ticketSourcesResponse.data;

        // // Get ticket time frames
        // const timeFramesResponse = await axios.get(`ticket/timeframes`);
        // this.timeFrames = timeFramesResponse.data;
        // const docs = await axios.get(`documents/${this.ticketId}`);
        // this.ticket.docs = docs.data;
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        this.$updateLoading(-1);
        console.log('loading stopped');
      }
    },
    async fetchTicket(ticketId) {
      this.$updateLoading(1);
      try {
        const response = await axios.get(`ticket/${ticketId}`);
        this.ticket = response.data;
        this.originalAlternatePhone = (response.data.alternatephone || '').toString();
        if (!this.canViewTicket(this.ticket)) {
          this.toast.error('You do not have permission to view this ticket');
          this.redirectToaddTicket();
          return;
        }
        // Check if user is external and ticket is internal
        if (!this.isInternalUser && this.ticket.internal === 1) {
          // Unauthorized access - redirect to ticket list with message
          console.log(this.isInternalUser);
          alert('You do not have permission to view this ticket.');
          this.$router.push('/viewTickets');
          return;
        }

        // Continue with ticket data processing
        // Requester (contact) can be null, e.g. a ticket created with no requester
        this.ticket.companyname = response.data.client?.companyname ?? '';
        this.ticket.tickettype = response.data.ticket_type?.description ?? '';
        this.ticket.requester = response.data.contact?.name ?? '';
        this.ticket.intrequester = '';
        this.ticket.watchersList = response.data.watchers || [];
        this.ticket.docs = [];
        await this.fetchDocuments(ticketId);
      } catch (error) {
        console.error("There was an error fetching ticket:", error);
      } finally {
        this.$updateLoading(-1);
      }
    },
    async fetchDocuments(ticketId) {
      try {
        const response = await axios.get(`documents/${ticketId}`);
        this.ticket.docs = response.data;
      } catch (error) {
        console.error("There was an error fetching documents:", error);
      }
    },
    async fetchClients() {
      try {
        const clientsResponse = await axios.get('clients/', { params: { include_inactive: 1 } });
        this.clients = clientsResponse.data;
      } catch (error) {
        console.error("There was an error fetching clients:", error);
      }
    },
    onKeyDown(field, direction) {
      const state = this.dropdownState[field];
      if (!state.visible || state.items.length === 0) return;

      if (direction === 'down') {
        state.index = (state.index + 1) % state.items.length;
      } else if (direction === 'up') {
        state.index = (state.index - 1 + state.items.length) % state.items.length;
      }
      this.$nextTick(() => {
        const container = this.$el.querySelector(`.scrollable-requester-list[data-field="${field}"]`);
        const activeItem = container?.querySelector('.active-item');
        activeItem?.scrollIntoView({ block: 'nearest' });
      });
    },

    onEnter(field, selectFn, nextRef = null, ...args) {
      const state = this.dropdownState[field];
      if (state.index >= 0 && state.index < state.items.length) {
        selectFn(state.items[state.index], ...args);
        state.index = -1;
        if (nextRef) {
          this.$nextTick(() => this.$refs[nextRef]?.focus());
        }
      }
    },

    resetDropdownState(field) {
      this.dropdownState[field].index = -1;
      this.dropdownState[field].visible = false;
    },
    handleFileUpload(event) {
      const files = Array.from(event.target.files || []);
      if (!files.length) return;

      // One doc entry per selected file; +index keeps ids unique within a pick.
      const newDocuments = files.map((file, index) => ({
        id: Date.now() + index, // Mock ID, using timestamp to simulate uniqueness
        doc_name: file.name,
        file: file,
        isNew: true
      }));

      // Directly push the new documents into the currentTicket's documents array
      if (this.ticket.docs) {
        this.ticket.docs.push(...newDocuments);
      } else {
        // Initialize the documents array if it doesn't exist
        this.ticket = { ...this.ticket, docs: newDocuments };
      }
      if (this.$refs.fileInput) this.$refs.fileInput.value = null;
    },
    setClient(client) {
      this.ticket.company_id = client.id;
      this.ticket.companyname = client.companyname;
      // searchReqTypeQuery.value = type.description;
      // this.showClientDropdown = false;
      this.dropdownState.client.visible = false;
    },
    clearClient() {
      this.ticket.company_id = '';
      this.ticket.companyname = '';
    },
    setReqType(type) {
      this.ticket.tickettypeid = type.id;
      this.ticket.tickettype = type.description;
      // searchReqTypeQuery.value = type.description;
      // this.showReqTypeDropdown = false;
      this.dropdownState.type.visible = false;
    },
    clearReqType() {
      this.ticket.tickettypeid = '';
      this.ticket.tickettype = '';
    },
    openTicketsModal() {
      if (this.ticket.attachTo == null)
        this.ticket.attachTo = 0;
      this.showTicketsModal = true;
    },
    closeClientDropdown() {
      // this.showClientDropdown = false;
      this.dropdownState.client.visible = false;
    },
    closeTypeDropdown() {
      // this.showReqTypeDropdown = false;
      this.dropdownState.type.visible = false;
    },
    closeReqDropdown() {
      // this.showReqDropdown = false;
      this.dropdownState.requester.visible = false;
    },
    handleClose() {
      this.showTicketsModal = false;
    },
    closeWatcherDropdown() {
      this.dropdownState.watcher.visible = false;
    },
    closeIntReqDropdown() {
      // this.showIntReqDropdown = false;
      this.dropdownState.internalRequester.visible = false;
    },
    toLocalMidnight(dateStr) {
      if (!dateStr) return null;
      const [y, m, d] = dateStr.split('-').map(Number);
      return new Date(y, m - 1, d, 0, 0, 0, 0);
    },

    localTodayMidnight() {
      const t = new Date();
      t.setHours(0, 0, 0, 0);
      return t;
    },
    // toggleWatcherDropdown () {
    //   this.showWatcherDropdown = !this.showWatcherDropdown;
    // },
    getNextBusinessDayOffset() {
      const today = new Date()
      const day = today.getDay()

      if (day === 5) return 3
      if (day === 6) return 2
      return 1
    },
    handleRelatedTickets(payload) {
      this.ticket.attachTo = payload?.id || 0;
      this.ticket.attachedRelatedTicket = payload?.ticket || null;
      this.showTicketsModal = false;
    },
    clearReq() {
      this.ticket.requester = '';
      this.ticket.contact_id = '';
      this.ticket.email = '';
      this.ticket.alternatephone = '';
    },
    clearIntReq() {
      this.ticket.intrequester = '';
      this.ticket.contact_id = '';
      this.ticket.email = '';
      this.ticket.phone = '';
    },
    setReq(req, isInternal = false) {
      if (isInternal) {
        this.ticket.intrequester = req.name;
        this.ticket.requester = '';
      } else {
        this.ticket.requester = req.name;
        this.ticket.intrequester = '';
      }
      this.ticket.contact_id = req.id;
      this.ticket.email = req.email;
      // searchReqTypeQuery.value = type.description;
      // this.showReqDropdown = false;
      // this.showIntReqDropdown = false;
      this.dropdownState.requester.visible = false;
      this.dropdownState.internalRequester.visible = false;
      this.ticket.alternatephone = req.phone_primary ? req.phone_primary : req.phone_secondary ? req.phone_secondary : ''; // Reset phone
    },
    confirmRequesterChange(req, isInternal = false) {
      if (this.ticket.contact_id && this.ticket.contact_id != req.id) {
        this.selectedRequester = req;
        console.log('1');
        // Find the old requester by contact_id
        const oldRequester = this.requesters.find(r => r.id === this.ticket.contact_id);
        if (oldRequester) {
          this.ticket.oldRequester = { id: oldRequester.id, name: oldRequester.name, visible: true };
        } else {
          this.ticket.oldRequester = { id: this.ticket.contact_id, name: this.ticket.requester, visible: true };
        }
        this.showReqConfirmationModal = true;
        this.selectedRequester.isInternal = isInternal;
      } else {
        console.log('2');
        this.setReq(req, isInternal);
      }
    },
    handleReqConfirmation(addOldRequesterAsWatcher) {
      if (addOldRequesterAsWatcher) {
        this.ticket.watchersList.push(this.ticket.oldRequester);
      }
      const req = this.selectedRequester;
      this.setReq(req, req.isInternal || false);
      this.showReqConfirmationModal = false;
    },
    toggleWatcher() {
      this.showWatcher = !this.showWatcher;
    },
    addWatcher(watcher) {
      if (!this.ticket.watchersList.some(w => w.id === watcher.id)) {
        this.ticket.watchersList.push({ id: watcher.id, name: watcher.name, visible: true });
      }
      // this.showWatcherDropdown = false;
      this.dropdownState.watcher.visible = false;
      this.watchersInput = '';
    },
    removeWatcher(watcher) {
      const watcherIndex = this.ticket.watchersList.findIndex(w => w.id === watcher.id);
      if (watcherIndex !== -1) {
        this.ticket.watchersList[watcherIndex].visible = false;
      }
    },
    handleFocus(field) {
      this.ticket['old' + field] = this.ticket[field];
      console.log(this.ticket['old' + field])
      this.showConfirmationModal = false;
    },
    handleBlur(field) {
      if (field === 'phone') return;
      if (this.ticket[field]) {
        this.fieldToConfirm = field;
        this.valueToConfirm = this.ticket[field];
        this.showConfirmationModal = true;
      }
    },
    handlePhoneOverrideDecision(updateAddressbook) {
      this.updateAddressbookPhone = updateAddressbook ? 1 : 0;
      this.phoneOverrideDecisionMade = true;
      this.showPhoneOverrideModal = false;
      // The submit was deferred while we waited for the answer — resume it now.
      this.submitTicket();
    },
    handleUpdateUserConfirm(data) {
      // Logic to handle the confirmation action
      this.ticket[data.field] = this.ticket[data.field]
      console.log('return to handleUpdateUserConfirm, make a post api call to update');
      this.showConfirmationModal = false;
    },
    handleUpdateUserCancel(data) {
      // Logic to handle the cancellation action
      console.log(data.field)
      this.ticket[data.field] = this.ticket['old' + data.field]
      this.showConfirmationModal = false;
    },
    handleAddUserCompletion({ newUser }) {
      console.log(newUser.email)
      this.ticket.requester = newUser.firstName + ' ' + newUser.lastName;
      this.ticket.email = newUser.email;
      this.ticket.alternatephone = newUser.phone;
      this.ticket.contact_id = 0;
      // call the api that adds the user
      // call the api to get updated list of requesters
      // Close the modal and reset the active textarea identifier
      this.showAddUserModal = false;
    },
    activateTextArea(key) {
      this.activeTextarea = key;
      this.showTextArea = true;
    },
    getTextareaInitialText() {
      switch (this.activeTextarea) {
        case "requester":
          return this.ticket.description;
        case "aimNotesPublic":
          return this.ticket.delegatornotes;
        case "privateNotes":
          return this.ticket.privateinfo;
        default:
          return "";
      }
    },
    handleTextCompletion({ updatedText, key }) {
      const fieldMapping = {
        requester: 'description',
        aimNotesPublic: 'delegatornotes',
        privateNotes: 'privateinfo'
      };
      const dbField = fieldMapping[key]; // Get the database field name from the mapping
      const oldText = this.ticket[dbField]; // Use the correct field name to get the old value

      // Check if the text actually changed before making an API call
      if (updatedText !== oldText) {
        this.ticket[dbField] = updatedText; // Update the value in the current ticket
        // this.changedNotes(dbField); // Call the helper function to update the database
      }

      // Close the modal and reset the active textarea identifier
      this.showTextArea = false;
      this.activeTextarea = null;
    },
    getCurrentLocalTime() {
      const now = new Date();
      return { days: 0, time: now.toTimeString().slice(0, 5) }; // HH:MM format of current time
    },
    getEndOfWeekFriday() {
      const today = new Date();
      const currentDay = today.getDay();
      const daysUntilFriday = (5 - currentDay + 7) % 7; // Calculate days until next Friday
      return { days: daysUntilFriday, time: '17:00' };
    },
    getEndOfNextWeekFriday() {
      const today = new Date();
      const currentDay = today.getDay();
      const daysUntilNextFriday = ((5 - currentDay + 7) % 7) + 7; // Calculate days until the Friday after next
      return { days: daysUntilNextFriday, time: '17:00' };
    },
    updatePromisedTime() {
      const today = new Date();
      const dayOffset = this.selectedDay === '30' ? 30
        : this.selectedDay === '60' ? 60
          : this.selectedDay === '90' ? 90
            : parseInt(this.selectedDay);
      today.setDate(today.getDate() + dayOffset);

      const monthStr = (today.getMonth() + 1).toString().padStart(2, '0');
      const dayStr = today.getDate().toString().padStart(2, '0');
      const dateStr = `${today.getFullYear()}-${monthStr}-${dayStr}`;

      let timeStr = '12:00';

      if (this.selectedTime !== '-1') {
        timeStr = this.selectedTime;
      } else if (this.ticket.promised != '' && this.ticket.promised != '0000-00-00 00:00:00') {
        console.log('coming in here');
        const existing = new Date(this.ticket.promised);
        const hours = existing.getHours().toString().padStart(2, '0');
        const minutes = existing.getMinutes().toString().padStart(2, '0');
        timeStr = `${hours}:${minutes}`;
      }

      this.ticket.promised = `${dateStr}T${timeStr}`;
    },
    formatForDatetimeLocal(date) {
      const d = date.toLocaleDateString('en-CA'); // YYYY-MM-DD
      const t = date.toLocaleTimeString('en-CA', { hour12: false, hour: '2-digit', minute: '2-digit' }); // HH:mm
      return `${d}T${t}`;
    },
    setPromisedDateTime(daysOffset, time) {
      this.selectedTime = '-1'
      this.selectedDay = '-1'
      const promisedDate = new Date();
      promisedDate.setDate(promisedDate.getDate() + daysOffset);

      // Convert to local timezone
      const [hours, minutes] = time.split(':');
      promisedDate.setHours(hours, minutes, 0, 0);

      // // Adjust to user's local timezone
      // const localOffset = promisedDate.getTimezoneOffset() * 60000;
      // const localTime = new Date(promisedDate.getTime() - localOffset);
      // const localISOTime = localTime.toISOString().slice(0, 16);

      this.ticket.promised = this.formatForDatetimeLocal(promisedDate);
    },
    deleteDocument(document) {
      if (this.ticket.docs) {
        this.ticket.docs = this.ticket.docs.filter(doc => doc.id !== document.id);
        if (!this.ticket.deleteDocumentIds) {
          this.ticket.deleteDocumentIds = [];
        }
        this.ticket.deleteDocumentIds.push(document.id);
      }
    },
    addSubTicket() {
      this.ticket.subTickets.push({
        id: Date.now(),
        description: '',
        promisedDate: '',
        estimatedHours: ''
      });
    },
    deleteSubTicket(index) {
      this.ticket.subTickets.splice(index, 1);
    },
    validateTicket() {
      const requiredFields = [
        { field: 'email', label: 'Email' },
        // { field: 'alternatephone', label: 'Phone' },
        // { field: 'requester', label: 'Requester' },
        { field: 'promised', label: 'Promised Date' },
        // { field: 'deadline', label: 'Deadline Date' },
        // { field: 'source', label: 'Source' },
        { field: 'tech_id', label: 'Assign To' },
        { field: 'company_id', label: 'Client' },
        { field: 'description', label: 'Description' },
      ];

      const missingFields = requiredFields.filter((field) => {
        if (field.field === 'tech_id') {
          return this.ticket.tech_id === '' || this.ticket.tech_id === null;
        }
        return !this.ticket[field.field];
      });

      if (!this.ticket.requester && !this.ticket.intrequester) {
        missingFields.push({ field: 'requester', label: 'Requester' });
      }


      if (missingFields.length > 0) {
        const fieldNames = missingFields.map((field) => field.label).join(', ');
        alert(`The following fields are required: ${fieldNames}`);
        return false; // Validation failed
      }

      return true; // Validation passed
    },
    async submitTicket() {
      if (!this.ticket.requester && this.ticket.intrequester) {
        this.ticket.requester = this.ticket.intrequester;
      }
      if (!this.validateTicket()) {
        return; // Stop execution if validation fails
      }
      const newPhone = (this.ticket.alternatephone || '').toString().trim();
      const oldPhone = (this.originalAlternatePhone || '').toString().trim();
      if (!this.phoneOverrideDecisionMade && newPhone && newPhone !== oldPhone) {
        this.showPhoneOverrideModal = true;
        return;
      }
      this.$updateLoading(1);
      try {
        const formData = new FormData();
        for (const key in this.ticket) {
          if (key !== 'docs' && key !== 'watchersList' && key !== 'subTickets' && key !== 'deleteDocumentIds') {
            formData.append(key, this.ticket[key]);
          }
          if (key === 'docs') {
            for (const doc of this.ticket.docs) {
              formData.append('docs[]', doc.file);
              if (doc.isNew) {
                formData.append('ticketdoc[]', doc.file, doc.file.name);
              }
            }
          }
          if (key === 'deleteDocumentIds') {
            this.ticket.deleteDocumentIds.forEach(doc => {
              formData.append('deleteDocumentIds[]', doc);
            });
          }
          if (key === 'watchersList') {
            this.ticket.watchersList.forEach(watcher => {
              formData.append('watchers[]', JSON.stringify({ id: watcher.id, visible: watcher.visible }));
            });
          }
        }
        formData.append('update_addressbook_phone', this.updateAddressbookPhone);
        const response = await axios.post('ticket/edit', formData);
        // Redirect to /viewTickets
        const currentDomain = window.location.origin;
        const newPath = '/viewTickets';
        window.location.href = `${currentDomain}${newPath}`;
      } catch (error) {
        console.error('Error editing ticket:', error);
        alert('Failed to edit ticket');
      }
      this.$updateLoading(-1);
    },
    canViewTicket(ticket) {
      if (!ticket) return false;
      if (Number(ticket.unpublished) === 0) return true;
      const userId = Number(localStorage.getItem('user_id') || 0);
      const techId = Number(ticket.tech_id || 0);
      const hasRole10 = Array.isArray(this.userroles) && this.userroles.includes(10);

      return techId === userId || hasRole10;
    },

    redirectToaddTicket() {
      this.$router.push({ name: 'newTicket' });
    }
  },
  async mounted() {
    this.ticketId = useRoute().params.id;
    this.userId = localStorage.getItem('user_id');
    await Promise.all([
      this.fetchUserRoles(),
      this.checkUserType(),
      this.fetchInitialData(),
    ]);
    await this.fetchTicket(this.ticketId);
  },
  computed: {
    // Current local time as "YYYY-MM-DDTHH:mm" — used as the max for the Submitted Date
    // input so a future submission date/time cannot be selected.
    maxSubmittedDateTime() {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    groupedTechnicians() {
      const groups = {};
      const ungrouped = [];

      this.technicians.forEach(tech => {
        if (!tech.deptId) {
          ungrouped.push({
            id: tech.id,
            name: tech.name,
          });
          return;
        }

        let groupKey = '';

        if (tech.deptId === "External Tech") {
          groupKey = "External Tech";
        } else if (tech.details) {
          groupKey = tech.details;
        } else {
          groupKey = "Other Department";
        }

        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }

        let displayName = tech.name;
        if (tech.deptId === "External Tech" && tech.details) {
          displayName += ` (${tech.details})`;
        }

        groups[groupKey].push({
          id: tech.id,
          name: displayName,
        });
      });

      if (ungrouped.length > 0) {
        groups[""] = ungrouped;
      }

      let keys = Object.keys(groups).filter(k => k !== "External Tech").sort((a, b) => {
        if (a === "") return -1;
        if (b === "") return 1;
        return a.localeCompare(b);
      });

      if (groups["External Tech"]) {
        keys.push("External Tech");
      }

      const orderedGroups = {};
      keys.forEach(key => {
        orderedGroups[key] = groups[key];
      });

      return orderedGroups;
    },
    filteredSprints() {
      const today = this.localTodayMidnight();

      return (this.sprints || [])
        .filter(s => {
          const end = this.toLocalMidnight(s.enddate);
          return end && end > today;
        })
        .sort((a, b) => {
          const aStart = this.toLocalMidnight(a.startdate)?.getTime() || 0;
          const bStart = this.toLocalMidnight(b.startdate)?.getTime() || 0;
          return aStart - bStart;
        });
    },
    isCoManagedTech() {
      return this.userroles.includes(80);
    },
    internalCheckboxModel: {
      get() {
        return this.ticket.internal === 1;
      },
      set(newValue) {
        this.ticket.internal = newValue ? 1 : 0;
        if (newValue) this.ticket.emailnotification = 0;
      }
    },
    filteredClients() {
      const filtered = this.ticket.companyname
        ? this.clients.filter((e) =>
          e.companyname
            .toLowerCase()
            .includes(this.ticket.companyname.toLowerCase())
        )
        : this.clients;
      this.dropdownState.client.items = filtered;
      return filtered;
    },
    filteredReqTypes() {
      const filtered = this.ticket.tickettype
        ? this.ticketTypes.filter((e) =>
          e.description
            .toLowerCase()
            .includes(this.ticket.tickettype.toLowerCase())
        )
        : this.ticketTypes;
      this.dropdownState.type.items = filtered;
      return filtered;
    },
    filteredRequesters() {
      const q = (this.ticket.requester || '').toLowerCase();
      const filtered = q
        // Typing: search across ALL requesters system-wide, the selected client's
        // own people listed first.
        ? searchRequesters(this.requesters, q, this.ticket.company_id, { clientList: this.allRequesters })
        // Empty: this client's requesters when a client is selected, else all.
        : (this.ticket.company_id ? this.allRequesters : this.requesters);
      this.dropdownState.requester.items = filtered;
      return filtered;
    },
    filteredIntRequesters() {
      if (this.isCoManagedTech) {
        const filtered = this.ticket.intrequester
          ? this.intRequesters.filter((e) =>
            e.name.toLowerCase().includes(this.ticket.intrequester.toLowerCase())
          ).slice(0, 20)
          : this.intRequesters;
        this.dropdownState.internalRequester.items = filtered;
        return filtered;
      }

      const filtered = this.ticket.intrequester
        ? this.requesters.filter((e) =>
          e.name.toLowerCase().includes(this.ticket.intrequester.toLowerCase())
        ).slice(0, 20)
        : this.intRequesters;
      this.dropdownState.internalRequester.items = filtered;
      return filtered;
    },
    filteredWatchers() {
      // Typing: system-wide search, the selected client's own people first.
      const filtered = this.watchersInput
        ? searchRequesters(this.requesters, this.watchersInput, this.ticket.company_id, { clientList: this.allRequesters })
        : this.allRequesters;
      this.dropdownState.watcher.items = filtered;
      return filtered;
    },
    // Private/internal tickets never email the requester or watchers (backend rule),
    // so Notify is shown off and disabled while either box is checked.
    notifyBlocked() {
      return this.ticket.unpublished === 1 || this.ticket.internal === 1;
    },
    notifyCheckboxModel: {
      get() {
        return !this.notifyBlocked && this.ticket.emailnotification === 1;
      },
      set(newValue) {
        this.ticket.emailnotification = newValue ? 1 : 0;
      }
    },
    unPublishedCheckboxModel: {
      get() {
        return this.ticket.unpublished === 1;
      },
      set(newValue) {
        this.ticket.unpublished = newValue ? 1 : 0;
        if (newValue) this.ticket.emailnotification = 0;
      }
    },
    urgencyCheckboxModel: {
      get() {
        return this.ticket.urgent === 1;
      },
      set(newValue) {
        this.ticket.urgent = newValue ? 1 : 0;
      }
    }
  },
  watch: {
    'ticket.company_id': function (newVal, oldVal) {
      if (!newVal) return;
      this.$updateLoading(1);
      // Drop the previous client's people now, so the requester typeahead doesn't
      // rank them first for the new client while this list is still loading.
      this.allRequesters = [];
      const apiCalls = [
        axios.get(`requesters/${newVal}`)
          .then((response) => {
            this.allRequesters = response.data;
          })
          .catch((error) => {
            console.error("There was an error fetching requesters:", error);
          }),

        axios.get(`ticket/get-types/${newVal}`)
          .then((response) => {
            this.ticketTypes = response.data;
          })
          .catch((error) => {
            console.error("There was an error fetching ticket types:", error);
          }),

        axios.get(`${axios.defaults.baseURL}user/active/${newVal}`)
          .then((response) => {
            this.technicians = response.data;
          })
          .catch((error) => {
            console.error("There was an error fetching technicians:", error);
          }),

        axios.get(`${axios.defaults.baseURL}projects/${newVal}`)
          .then((response) => {
            this.projects = response.data;
          })
          .catch((error) => {
            console.error("There was an error fetching projects related to a company:", error);
          })
      ];

      Promise.all(apiCalls)
        .finally(() => {
          // Decrement the loading counter once all API calls are finished
          this.$updateLoading(-1);
        });
    },
    userroles(newVal, oldVal) {
      if (!newVal || this.isCoManagedTech) return;
      this.$updateLoading(1);
      const apiCalls = [
        axios.get(`requesters/internal`)
          .then((response) => {
            this.intRequesters = response.data;
          })
          .catch((error) => {
            console.error("There was an error fetching requesters:", error);
          }),
      ];

      Promise.all(apiCalls)
        .finally(() => {
          // Decrement the loading counter once all API calls are finished
          this.$updateLoading(-1);
        });
    },
    selectedDay(newVal, oldVal) {
      if (newVal !== '-1') {
        this.updatePromisedTime();
      }
    },
    selectedTime(newVal, oldVal) {
      if (newVal !== '-1') {
        this.updatePromisedTime();
      }
    },
    // 'ticket.selectedTimeFrameID': function (newVal) {
    //   if (newVal && this.timeframesMapping[newVal]) {
    //     const { days, time } = this.timeframesMapping[newVal];
    //     this.setPromisedDateTime(days, time);
    //   }
    // },
  }
};
</script>

<style scoped>
.scrollable-requester-list {
  max-height: 400px;
  /* Adjust this value based on your design needs */
  overflow-y: auto;
  /* Enables vertical scrolling */
  border: 1px solid #ccc;
  /* Optional: adds a border around the list */
}
</style>